import { parseScopedThreadKey, scopeProjectRef } from "@cmmd-forge/client-runtime";
import { type ScopedThreadRef, ThreadId } from "@cmmd-forge/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useCallback } from "react";

import { pickPostDeleteNavigation } from "./useThreadActions.deleteNavigation";
import { useThreadLifecycleActions } from "./useThreadLifecycleActions";
import { useComposerDraftStore } from "../composerDraftStore";
import { ensureEnvironmentApi, readEnvironmentApi } from "../environmentApi";
import { invalidateGitQueries } from "../lib/gitReactQuery";
import { newCommandId } from "../lib/utils";
import { readLocalApi } from "../localApi";
import {
  selectProjectByRef,
  selectThreadByRef,
  selectThreadsForEnvironment,
  useStore,
} from "../store";
import { useTerminalStateStore } from "../terminalStateStore";
import { buildThreadRouteParams, resolveThreadRouteRef } from "../threadRoutes";
import { formatWorktreePathForDisplay, getOrphanedWorktreePathForThread } from "../worktreeCleanup";
import { toastManager } from "../components/ui/toast";
import { useSettings } from "./useSettings";
import type { SidebarThreadSummary } from "../types";
import { resolveThreadOrThrow, type ResolvedThreadTarget } from "./useThreadActions.archive";

type DeleteThreadSnapshot = Pick<SidebarThreadSummary, "environmentId" | "id" | "projectId"> & {
  readonly session?: SidebarThreadSummary["session"];
  readonly worktreePath?: string | null;
};

interface DeleteThreadOptions {
  readonly deletedThreadKeys?: ReadonlySet<string>;
  /** The Activity sidebar can render CMMD replicas that are not in the live store. */
  readonly threadSnapshot?: DeleteThreadSnapshot;
}

type LiveThread = NonNullable<ReturnType<typeof selectThreadByRef>>;

function resolveDeletedThreadIds(
  environmentId: string,
  deletedThreadKeys: ReadonlySet<string> | undefined,
): ReadonlySet<ThreadId> | undefined {
  if (!deletedThreadKeys || deletedThreadKeys.size === 0) return undefined;
  return new Set<ThreadId>(
    [...deletedThreadKeys].flatMap((threadKey) => {
      const ref = parseScopedThreadKey(threadKey);
      return ref && ref.environmentId === environmentId ? [ref.threadId] : [];
    }),
  );
}

function excludeDeletedThreads(
  threads: ReadonlyArray<LiveThread>,
  target: ScopedThreadRef,
  deletedIds: ReadonlySet<ThreadId> | undefined,
): ReadonlyArray<LiveThread> {
  if (!deletedIds || deletedIds.size === 0) return threads;
  return threads.filter((thread) => thread.id === target.threadId || !deletedIds.has(thread.id));
}

async function shouldDeleteOrphanedWorktree(input: {
  readonly canDeleteWorktree: boolean;
  readonly displayWorktreePath: string | null;
  readonly localApi: NonNullable<ReturnType<typeof readLocalApi>> | null | undefined;
  readonly orphanedWorktreePath: string | null;
}): Promise<boolean> {
  if (!input.canDeleteWorktree || !input.localApi || !input.orphanedWorktreePath) return false;
  return input.localApi.dialogs.confirm(
    [
      "This thread is the only one linked to this worktree:",
      input.displayWorktreePath ?? input.orphanedWorktreePath,
      "",
      "Delete the worktree too?",
    ].join("\n"),
  );
}

async function stopThreadSession(
  api: NonNullable<ReturnType<typeof readEnvironmentApi>>,
  thread: { readonly session?: SidebarThreadSummary["session"] },
  threadRef: ScopedThreadRef,
): Promise<void> {
  if (!thread.session || thread.session.status === "closed") return;
  await api.orchestration
    .dispatchCommand({
      type: "thread.session.stop",
      commandId: newCommandId(),
      threadId: threadRef.threadId,
      createdAt: new Date().toISOString(),
    })
    .catch(() => undefined);
}

async function closeThreadTerminal(
  api: NonNullable<ReturnType<typeof readEnvironmentApi>>,
  threadRef: ScopedThreadRef,
): Promise<void> {
  try {
    await api.terminal.close({ threadId: threadRef.threadId, deleteHistory: true });
  } catch {
    // Terminal may already be closed.
  }
}

export function useThreadActions() {
  const sidebarThreadSortOrder = useSettings((settings) => settings.sidebarThreadSortOrder);
  const confirmThreadDelete = useSettings((settings) => settings.confirmThreadDelete);
  const clearComposerDraftForThread = useComposerDraftStore((store) => store.clearDraftThread);
  const clearProjectDraftThreadById = useComposerDraftStore(
    (store) => store.clearProjectDraftThreadById,
  );
  const clearTerminalState = useTerminalStateStore((state) => state.clearTerminalState);
  const router = useRouter();
  const queryClient = useQueryClient();

  const resolveThreadTarget = useCallback(
    (target: ScopedThreadRef): ResolvedThreadTarget | null => {
      const state = useStore.getState();
      const thread = selectThreadByRef(state, target);
      if (!thread) {
        return null;
      }
      return {
        thread,
        threadRef: target,
      };
    },
    [],
  );
  const getCurrentRouteThreadRef = useCallback(() => {
    const currentRouteParams = router.state.matches[router.state.matches.length - 1]?.params ?? {};
    return resolveThreadRouteRef(currentRouteParams);
  }, [router]);

  const { unarchiveThread, unsettleThread, archiveThread, settleThread } =
    useThreadLifecycleActions(resolveThreadTarget);

  const deleteThread = useCallback(
    async (target: ScopedThreadRef, opts: DeleteThreadOptions = {}) => {
      const api = readEnvironmentApi(target.environmentId);
      if (!api) throw new Error("The thread's runtime is unavailable for deletion.");
      const resolved = resolveThreadTarget(target);
      const thread = resolveThreadOrThrow(
        target,
        resolved,
        opts.threadSnapshot,
        "Thread snapshot does not match the deletion target.",
        "Thread is unavailable for deletion.",
      );
      const threadRef = target;
      const state = useStore.getState();
      const threads = selectThreadsForEnvironment(state, threadRef.environmentId);
      const threadProject = selectProjectByRef(state, {
        environmentId: threadRef.environmentId,
        projectId: thread.projectId,
      });
      const deletedIds = resolveDeletedThreadIds(threadRef.environmentId, opts.deletedThreadKeys);
      const survivingThreads = excludeDeletedThreads(threads, threadRef, deletedIds);
      const orphanedWorktreePath = getOrphanedWorktreePathForThread(
        survivingThreads,
        threadRef.threadId,
      );
      const displayWorktreePath = orphanedWorktreePath
        ? formatWorktreePathForDisplay(orphanedWorktreePath)
        : null;
      const canDeleteWorktree = orphanedWorktreePath !== null && threadProject !== undefined;
      const localApi = readLocalApi();
      const shouldDeleteWorktree = await shouldDeleteOrphanedWorktree({
        canDeleteWorktree,
        displayWorktreePath,
        localApi,
        orphanedWorktreePath,
      });

      await stopThreadSession(api, thread, threadRef);
      await closeThreadTerminal(api, threadRef);

      const deletedThreadIds = deletedIds ?? new Set<ThreadId>();
      const currentRouteThreadRef = getCurrentRouteThreadRef();

      await api.orchestration.dispatchCommand({
        type: "thread.delete",
        commandId: newCommandId(),
        threadId: threadRef.threadId,
      });
      clearComposerDraftForThread(threadRef);
      clearProjectDraftThreadById(
        scopeProjectRef(threadRef.environmentId, thread.projectId),
        threadRef,
      );
      clearTerminalState(threadRef);

      // Decide where to send the SPA after the delete dispatch. The helper
      // also re-checks visibility of the fallback against the post-dispatch
      // store, so a thread archived in another tab between our pre-dispatch
      // snapshot and now doesn't become a phantom route target.
      const navigation = pickPostDeleteNavigation({
        threads,
        deletedThreadRef: threadRef,
        currentRouteThreadRef,
        deletedThreadIds,
        sortOrder: sidebarThreadSortOrder,
        lookupThreadByRef: (ref) => selectThreadByRef(useStore.getState(), ref) ?? null,
      });
      if (navigation.kind === "navigate-to-thread") {
        await router.navigate({
          to: "/$environmentId/$threadId",
          params: buildThreadRouteParams(navigation.ref),
          replace: true,
        });
      } else if (navigation.kind === "navigate-to-root") {
        await router.navigate({ to: "/", replace: true });
      }

      if (!shouldDeleteWorktree || !orphanedWorktreePath || !threadProject) {
        return;
      }

      try {
        await ensureEnvironmentApi(threadRef.environmentId).git.removeWorktree({
          cwd: threadProject.cwd,
          path: orphanedWorktreePath,
          force: true,
        });
        await invalidateGitQueries(queryClient, {
          environmentId: threadRef.environmentId,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error removing worktree.";
        console.error("Failed to remove orphaned worktree after thread deletion", {
          threadId: threadRef.threadId,
          projectCwd: threadProject.cwd,
          worktreePath: orphanedWorktreePath,
          error,
        });
        toastManager.add({
          type: "error",
          title: "Thread deleted, but worktree removal failed",
          description: `Could not remove ${displayWorktreePath ?? orphanedWorktreePath}. ${message}`,
        });
      }
    },
    [
      clearComposerDraftForThread,
      clearProjectDraftThreadById,
      clearTerminalState,
      getCurrentRouteThreadRef,
      router,
      queryClient,
      resolveThreadTarget,
      sidebarThreadSortOrder,
    ],
  );

  const confirmAndDeleteThread = useCallback(
    async (target: ScopedThreadRef) => {
      const api = readEnvironmentApi(target.environmentId);
      if (!api) return;
      const localApi = readLocalApi();
      const resolved = resolveThreadTarget(target);
      if (!resolved) return;
      const { thread } = resolved;

      if (confirmThreadDelete && localApi) {
        const confirmed = await localApi.dialogs.confirm(
          [
            `Delete thread "${thread.title}"?`,
            "This permanently clears conversation history for this thread.",
          ].join("\n"),
        );
        if (!confirmed) {
          return;
        }
      }

      await deleteThread(target);
    },
    [confirmThreadDelete, deleteThread, resolveThreadTarget],
  );

  return {
    archiveThread,
    unarchiveThread,
    settleThread,
    unsettleThread,
    deleteThread,
    confirmAndDeleteThread,
  };
}
